import { CompanyStatus } from "@pentacode/core";
import { css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Checkbox } from "../checkbox";
import { Dialog } from "../dialog";

export interface BetaToggleResult {
    confirmed: boolean;
    notifyOwner: boolean;
    notifyManagers: boolean;
}

export type BetaToggleCompany = {
    isBetaCustomer: boolean;
    name: string;
    effectiveCompanyStatus: CompanyStatus;
};

@customElement("ptc-beta-toggle-dialog")
export class BetaToggleDialog extends Dialog<BetaToggleCompany, BetaToggleResult> {
    @state()
    private _notifyOwner = false;

    @state()
    private _notifyManagers = false;

    @state()
    private _companyActive = false;

    static styles = [
        ...Dialog.styles,
        Checkbox.styles,
        css`
            .checkbox-container {
                padding-bottom: 1em;
            }
        `,
    ];

    renderContent() {
        const { isBetaCustomer, name } = this.input;
        if (!name) {
            return html``;
        }

        return html`
            <div class="padded-medium spacing vertical layout">
                <div class="large text-centering">
                    ${isBetaCustomer ? "Beta-Status entfernen" : "Auf Beta-Kunde umstellen"}
                </div>

                <p class="text-centering">
                    ${isBetaCustomer
                        ? html`Soll der Beta-Status für "<strong>${name}</strong>" wirklich entfernt werden?`
                        : html`Soll "<strong>${name}</strong>" wirklich auf Beta gestellt werden?`}
                </p>

                <div class="double-margined checkbox-container">
                    <label>E-Mail Benachrichtigungen versenden an: </label>
                    <div class="spacing vertical layout">
                        <ptc-checkbox-button
                            label="Besitzer"
                            buttonClass="slim ghost"
                            .checked=${this._notifyOwner}
                            @change=${(e: Event) => (this._notifyOwner = (e.target as Checkbox).checked)}
                            ?disabled=${!this._companyActive}
                        ></ptc-checkbox-button>
                        <ptc-checkbox-button
                            label="Manager"
                            buttonClass="slim ghost"
                            .checked=${this._notifyManagers}
                            @change=${(e: Event) => (this._notifyManagers = (e.target as Checkbox).checked)}
                            ?disabled=${!this._companyActive}
                        ></ptc-checkbox-button>
                        ${(this._notifyOwner || this._notifyManagers) && this._companyActive
                            ? html`
                                  <p class="text-centering subtle small">
                                      Beim Speichern werden E-Mail Benachrichtigungen an die Besitzer und Manager des
                                      Unternehmens versendet.
                                  </p>
                              `
                            : null}
                        ${!this._companyActive
                            ? html`
                                  <p class="text-centering red colored-text small">
                                      Das Unternehmen ist weder aktiv noch in Testphase. E-Mail Benachrichtigungen
                                      werden nicht versendet.
                                  </p>
                              `
                            : null}
                    </div>
                </div>

                <div class="horizontal spacing evenly stretching layout">
                    <button class="primary" @click=${this._confirm}>Speichern</button>
                    <button @click=${this._cancel}>Abbrechen</button>
                </div>
            </div>
        `;
    }

    async show(input: BetaToggleCompany) {
        // default to notify owner and managers if company is active or trialing
        if (
            [CompanyStatus.TrialWithoutContract, CompanyStatus.Trialing, CompanyStatus.Active].some(
                (s) => s === input.effectiveCompanyStatus
            )
        ) {
            this._companyActive = true;
            this._notifyOwner = true;
            this._notifyManagers = true;
        }
        return super.show(input);
    }

    private _confirm() {
        this.done({
            confirmed: true,
            notifyOwner: this._notifyOwner,
            notifyManagers: this._notifyManagers,
        });
    }

    private _cancel() {
        this.done({
            confirmed: false,
            notifyOwner: false,
            notifyManagers: false,
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-beta-toggle-dialog": BetaToggleDialog;
    }
}
